import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnInit,
  ViewChild,
  AfterViewInit,
  TemplateRef,
  OnChanges,
} from '@angular/core';
import { DynamicFormComponent } from 'components/dynamic-form/dynamic-form.component';
import { LogService } from 'services/log/log.service';
import { MessageQueryService } from '@certemy/ui-components';
import { PAGES } from '../../../log-details/log-details.component';
import { ComputedFrom } from '@certemy/common';
import { COMPLIANCE_CRITERIA } from 'constants/log-step.constants';
import { LogStepBaseMixin } from 'common/mixins/log-step-base.mixin';
import _get from 'lodash-es/get';
import _cloneDeep from 'lodash-es/cloneDeep';
import _isFunction from 'lodash-es/isFunction';
import { VERIFICATION_TYPE } from 'constants/constants';
import { SignatureComponent } from 'components/signature/signature.component';

enum MODE {
  READ,
  EDIT,
}

@Component({
  selector: 'cm-crud-entry',
  templateUrl: './crud-entry.component.html',
  styleUrls: ['./crud-entry.component.scss', '../../../step-details.shared.scss'],
})
export class CrudEntryComponent extends LogStepBaseMixin implements OnInit, AfterViewInit, OnChanges {
  @Input() hideTitle: boolean;
  @Input() step; // Put Input step before entry;
  @Input() hideButtons: boolean;
  @Input() introSignature: TemplateRef<any>;
  @Input() entry;
  @Input() stepEditableStatus: boolean;
  @Output() goToPage: EventEmitter<any> = new EventEmitter();
  @ViewChild('dynamicForm') dynamicForm: DynamicFormComponent;
  @ViewChild(SignatureComponent) signatureComponent: SignatureComponent;

  @ComputedFrom('step')
  get isEntriesCriteria(): boolean {
    return this.step.content.compliance_criteria_type_id === COMPLIANCE_CRITERIA.ENTRIES;
  }

  mode: number;
  modes = MODE;
  formDisabled: boolean;
  private formControlCache;
  signatureError: string;

  constructor(private logService: LogService, private messageService: MessageQueryService) {
    super();
  }

  ngOnInit() {
    this.mode = MODE.READ;
    this.formDisabled = true;
  }

  ngOnChanges(changes) {
    if (changes.entry) {
      const fields = _get(this.entry, 'fields');
      if (fields) {
        this.entry.fields = this.mapFields(fields, _get(this.step, 'content', {}), this.step?.step_type_id);
      }
      this.updateFormAndSetSignature();
    }
  }

  ngAfterViewInit() {
    this.setSignature();
  }

  private updateFormAndSetSignature() {
    const needUpdate = !!this.entry;
    if (needUpdate) {
      this.mode = MODE.READ;
      this.formDisabled = true;
      if (this.dynamicForm) {
        this.dynamicForm.updateFormData(this.entry.fields, this.formDisabled);
      } else {
        setTimeout(() => {
          this.dynamicForm.updateFormData(this.entry.fields, this.formDisabled);
        });
      }
      setTimeout(() => {
        this.setSignature();
      });
    }
  }

  private setSignature() {
    const signatureContent = _get(this.entry, 'signature_link', '');

    if (signatureContent) {
      this.signatureComponent.signature = signatureContent;
    }
  }

  back() {
    this.goToPage.emit(PAGES.ENTRIES);
  }

  save(saveAsDraft = false, callback?: any) {
    const stepId = this.step.step_instance_id;
    const dynamicFormData = this.dynamicForm.getFormData(this.dynamicForm.dynamicForm);
    this.signatureError =
      !!this.entry.signature_link && this.signatureComponent && this.signatureComponent.getSignatureErrors();
    const preparedPayload = this.logService.prepareEntryPayload({
      type: 'single',
      fields: this.entry.fields,
      data: dynamicFormData,
      saveAsDraft,
      signature_link: !this.signatureError && this.signatureComponent && this.signatureComponent.signature,
    });

    if (saveAsDraft) {
      this.dynamicForm.showErrors = false;
    }

    if (_isFunction(callback)) {
      callback({
        saveAsDraft,
        form: this.dynamicForm,
        preparedPayload,
        signatureError: this.signatureError,
      });
      return;
    }
    if (saveAsDraft || (this.dynamicForm.checkFormForErrors() && !this.signatureError)) {
      this.logService.saveEntry(stepId, this.entry.id, preparedPayload).subscribe(({ remaining }) => {
        this.showMessageAfterFormSubmit({ isLastEntity: !remaining });
        this.goToPage.emit(PAGES.ENTRIES);
      });
    }
  }

  remove() {
    this.logService
      .removeEntry(this.step.step_instance_id, this.entry.id)
      .subscribe(() => this.goToPage.emit(PAGES.ENTRIES));
  }

  onEdit() {
    this.mode = MODE.EDIT;
    this.formControlCache = _cloneDeep(this.dynamicForm.dynamicForm.value);
    this.formDisabled = false;
    this.changeModeSignature(true);
  }

  onCancelEdit() {
    this.dynamicForm.restoreChanges(this.formControlCache);
    this.setSignature();
    this.mode = MODE.READ;
    this.formDisabled = true;
    this.changeModeSignature(false);
  }

  private changeModeSignature(isEnable) {
    if (this.signatureComponent) {
      this.signatureComponent.signaturePad[isEnable ? 'on' : 'off']();
    }
  }

  private showMessageAfterFormSubmit(showMessageConditions: { isLastEntity: boolean; isMultipleUpload?: boolean }) {
    if (showMessageConditions.isLastEntity) {
      this.messageService.addMessage(
        `All required ${this.isEntriesCriteria ? 'entries' : 'hours'} are submitted, please use
        ${
          [VERIFICATION_TYPE.VERIFICATION_REQUIRED, VERIFICATION_TYPE.DOUBLE_VERIFICATION_REQUIRED].includes(
            this.step.verificationType,
          )
            ? ''
            : ' complete'
        } option
        ${
          [VERIFICATION_TYPE.VERIFICATION_REQUIRED, VERIFICATION_TYPE.DOUBLE_VERIFICATION_REQUIRED].includes(
            this.step.verificationType,
          )
            ? ' to send them for verification'
            : ''
        } to complete step.`,
        6000,
      );
    } else {
      this.messageService.addMessage(
        `Your ${showMessageConditions.isMultipleUpload ? 'entries have' : 'entry has'} been successfully submitted`,
      );
    }
  }
}
